import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, Trash2, FileText, CheckCircle, AlertCircle, Clock, XCircle } from 'lucide-react'
import {
  PageHeader, SearchBar, Table, TableHeader, TableBody, TableRow, TableCell,
  Pagination, ListContainer, Button, Badge, LoadingSpinner, ErrorMessage,
  EmptyState, FormSelect, ConfirmationModal
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'RulesAndRegulations', label: 'Rules & Regulations' },
  { value: 'StrataDocuments', label: 'Strata Documents' },
  { value: 'Financial', label: 'Financial' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'MeetingMinutes', label: 'Meeting Minutes' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'General', label: 'General' },
]

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'Uploaded', label: 'Uploaded' },
  { value: 'Processing', label: 'Processing' },
  { value: 'Indexed', label: 'Indexed' },
  { value: 'Failed', label: 'Failed' },
]

const statusBadgeVariant = {
  Uploaded: 'default',
  Processing: 'warning',
  Indexed: 'success',
  Failed: 'danger',
  PendingDeletion: 'secondary',
}

const statusIcon = {
  Uploaded: Clock,
  Processing: Clock,
  Indexed: CheckCircle,
  Failed: XCircle,
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function KnowledgeDocuments() {
  const navigate = useNavigate()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedIds, setSelectedIds] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [pagination, setPagination] = useState({
    pageNumber: 1, pageSize: 12, totalCount: 0, totalPages: 0
  })

  const buildingId = localStorage.getItem('selectedBuildingId')

  useEffect(() => {
    if (buildingId) loadDocuments()
  }, [buildingId, pagination.pageNumber, searchTerm, selectedCategory, selectedStatus])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const params = {
        buildingId,
        PageNumber: pagination.pageNumber,
        PageSize: pagination.pageSize,
        ...(searchTerm && { SearchString: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedStatus && { status: selectedStatus }),
      }
      const response = await knowledgeBaseApi.getDocuments(params)
      setDocuments(response.data.data || [])
      setPagination(prev => ({
        ...prev,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages || Math.ceil(response.data.totalCount / prev.pageSize)
      }))
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    setPagination(prev => ({ ...prev, pageNumber: 1 }))
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === documents.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(documents.map(d => d.id))
    }
  }

  const handleBulkDelete = async () => {
    try {
      setDeleting(true)
      await knowledgeBaseApi.deleteMultipleDocuments(selectedIds)
      setSelectedIds([])
      setShowDeleteModal(false)
      loadDocuments()
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  if (loading && pagination.pageNumber === 1) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledge Base Documents"
        subtitle="Manage uploaded documents"
        backLink={{ label: 'Back to Knowledge Base', href: '/knowledge-base' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <SearchBar value={searchTerm} onChange={handleSearch} placeholder="Search documents..." />
        </div>
        <FormSelect value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setPagination(p => ({ ...p, pageNumber: 1 })) }} options={categoryOptions} />
        <FormSelect value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setPagination(p => ({ ...p, pageNumber: 1 })) }} options={statusOptions} />
        <Button variant="primary" icon={<Upload size={16} />} onClick={() => navigate('/knowledge-base/upload')}>Upload</Button>
        {selectedIds.length > 0 && (
          <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => setShowDeleteModal(true)}>
            Delete ({selectedIds.length})
          </Button>
        )}
      </div>

      <ListContainer>
        {documents.length === 0 ? (
          <EmptyState
            title="No documents found"
            description="Upload your first document to get started"
            actionLabel="Upload Document"
            onAction={() => navigate('/knowledge-base/upload')}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell header>
                  <input type="checkbox" checked={selectedIds.length === documents.length && documents.length > 0}
                    onChange={toggleSelectAll} className="rounded border-slate-300" />
                </TableCell>
                <TableCell header>File</TableCell>
                <TableCell header>Title</TableCell>
                <TableCell header>Category</TableCell>
                <TableCell header>Status</TableCell>
                <TableCell header>Size</TableCell>
                <TableCell header>Uploaded</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map(doc => {
                const StatusIcon = statusIcon[doc.status] || FileText
                return (
                  <TableRow key={doc.id} clickable onClick={() => navigate(`/knowledge-base/documents/${doc.id}`)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(doc.id)}
                        onChange={() => toggleSelect(doc.id)} className="rounded border-slate-300" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{doc.fileName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600 truncate max-w-[200px]">{doc.title || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-600">{categoryOptions.find(c => c.value === doc.category)?.label || doc.category}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant[doc.status] || 'default'}>
                        <StatusIcon size={12} className="mr-1" />
                        {doc.status}
                      </Badge>
                    </TableCell>
                    <TableCell><span className="text-sm text-slate-500">{formatFileSize(doc.fileSize)}</span></TableCell>
                    <TableCell><span className="text-sm text-slate-500">{new Date(doc.createdAt).toLocaleDateString()}</span></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </ListContainer>

      {documents.length > 0 && (
        <Pagination
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Documents"
        message={`Are you sure you want to delete ${selectedIds.length} document(s)? This will also remove them from S3.`}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />
    </div>
  )
}
